import $i18n from 'panda-i18n';
import React from 'react';
import styles from './index.module.scss';
import { dealSize, formatDate, transformToArray, transformWareHouse, vToLabel } from '@/utils';
import {
  VAS_DEAL_TYPE,
  VAS_OPERATE_PERIOD,
  VAS_PHOTOT_TYPE,
  VAS_SERVICE_KIND,
  VAS_SERVICE_TYPE,
  VAS_SKU_STOCK_QUALITY,
  VAS_TASK_PERIOD,
} from '@/const';
import { TableColumnProps } from '@/components/FpxTable2';

/**
 * 基础信息
 */
const basicItems = [
  {
    label: $i18n.get({ id: 'ServiceCategory.four_px', dm: '服务分类' }),
    dataIndex: 'businessType',
    transform: (value: string) => vToLabel(VAS_SERVICE_KIND, value),
  },

  {
    label: $i18n.get({ id: 'Warehouse.four_px', dm: '仓库' }),
    dataIndex: 'warehouseCode',
    transform: (value: string) => transformWareHouse(value),
  },

  {
    label: $i18n.get({ id: 'ValueAddedServiceNumber.four_px', dm: '增值服务单号' }),
    dataIndex: 'consignmentNo',
  },

  {
    label: $i18n.get({ id: 'CustomerOrderNo.four_px', dm: '客户订单号' }),
    dataIndex: 'refNo',
  },

  {
    label: $i18n.get({ id: 'ValueAddedServices.four_px', dm: '增值服务' }),
    dataIndex: 'serviceType',
    transform: (value: string) => vToLabel(VAS_SERVICE_TYPE, value),
  },

  {
    label: $i18n.get({ id: 'Segment.four_px', dm: '所属分段' }),
    dataIndex: 'operationSegment',
    transform: (value: string) => vToLabel(VAS_OPERATE_PERIOD, value),
  },

  {
    label: $i18n.get({ id: 'State.four_px', dm: '状态' }),
    dataIndex: 'status',
    transform: (value: string) => vToLabel(VAS_TASK_PERIOD, value),
  },

  {
    label: $i18n.get({ id: 'ActualCompletionTime.four_px', dm: '实际完成时间' }),
    dataIndex: 'completeTime',
    transform: (value: string) => formatDate(value),
  },

  {
    label: $i18n.get({ id: 'KHBM.CustomerCode.four_px', dm: '客户编码' }),
    dataIndex: 'customerCode',
  },

  {
    label: $i18n.get({ id: 'CustomerRemarks.four_px', dm: '客户备注' }),
    dataIndex: 'customerRemark',
  },
  {
    label: $i18n.get({ id: 'AssociatedNumber', dm: '关联单号' }),
    dataIndex: 'consignmentNo',
    slot: 'associatedOrder',
  },

  {
    label: $i18n.get({ id: 'InventoryMatching', dm: '库存匹配' }),
    slot: 'vasMatchInventory',
  },
];

/**
 * 增值服务预报信息 --- 列表
 */
/**
 * ===> 商品重新包装
 */
const RepackageColumn: TableColumnProps[] = [
  {
    title: 'No.',
    width: 70,
    cell: (value: string, index: number) => index + 1,
  },

  {
    title: $i18n.get({ id: 'AssociatedNumber', dm: '关联单号' }),
    dataIndex: 'relativeBusinessNo',
    width: 150,
  },

  {
    title: $i18n.get({ id: 'txt_sku_code', dm: 'SKU编码' }),
    dataIndex: 'skuCode',
    width: 150,
  },
  {
    title: 'SKU ID',
    dataIndex: 'skuId',
    width: 150,
  },
  {
    title: $i18n.get({ id: 'waveAnalyze.skuBatchNos', dm: '指定批次号' }),
    dataIndex: 'batchNo',
    width: 150,
    cell: (value) => {
      console.log('value==', value);
      return <>{value === "''" ? '-' : value}</>;
    },
  },

  {
    title: $i18n.get({ id: 'ProcessingType.four_px', dm: '处理类型' }),
    dataIndex: 'operationCode',
    width: 150,
    cell: (value: string) => vToLabel(VAS_DEAL_TYPE, value),
  },

  {
    title: $i18n.get({ id: 'DemandQuantity', dm: '需求数量' }),
    dataIndex: 'operationQty',
    width: 150,
  },

  {
    title: $i18n.get({ id: 'NumberOfOperations', dm: '操作数量' }),
    dataIndex: 'actualOperationQty',
    slot: 'actualOperationQty',
    width: 150,
  },
];

/**
 * ===> 拍照
 */
const PhotoColumn: TableColumnProps[] = [
  {
    title: 'No.',
    width: 70,
    cell: (value: string, index: number) => index + 1,
  },

  {
    title: $i18n.get({ id: 'AssociatedNumber', dm: '关联单号' }),
    dataIndex: 'relativeBusinessNo',
    width: 150,
  },

  {
    title: $i18n.get({ id: 'txt_sku_code', dm: 'SKU编码' }),
    dataIndex: 'skuCode',
    width: 150,
  },
  {
    title: 'SKU ID',
    dataIndex: 'skuId',
    width: 150,
  },
  {
    title: $i18n.get({ id: 'waveAnalyze.skuBatchNos', dm: '指定批次号' }),
    dataIndex: 'batchNo',
    width: 150,
    cell: (value) => {
      return <>{value === "''" ? '-' : value}</>;
    },
  },

  {
    title: $i18n.get({ id: 'ProcessingType.four_px', dm: '处理类型' }),
    dataIndex: 'operationCode',
    width: 150,
    cell: (value: string) => vToLabel(VAS_DEAL_TYPE, value),
  },

  {
    title: $i18n.get({ id: 'PictureDescription', dm: '图片描述' }),
    dataIndex: 'descriptionCode',
    width: 150,
    cell: (value: string) => {
      const descList = transformToArray(value || '');
      return descList?.map((e) => (
        <>
          {vToLabel(VAS_PHOTOT_TYPE, e)}
          <br />
        </>
      ));
    },
  },

  {
    title: $i18n.get({ id: 'NumberOfPhotoRequirements.four_px', dm: '拍照需求数量' }),
    dataIndex: 'operationQty',
    width: 150,
  },

  {
    title: $i18n.get({ id: 'NumberOfOperations', dm: '操作数量' }),
    dataIndex: 'actualOperationQty',
    slot: 'actualOperationQty',
    width: 150,
  },
];

/**
 * ===> 标签处理
 */
const LabelColumn: TableColumnProps[] = [
  {
    title: 'No.',
    width: 70,
    cell: (value: string, index: number) => index + 1,
  },

  {
    title: $i18n.get({ id: 'AssociatedNumber', dm: '关联单号' }),
    dataIndex: 'relativeBusinessNo',
    width: 150,
  },

  {
    title: $i18n.get({ id: 'SkuEncoding.four_px', dm: 'SKU编码' }),
    dataIndex: 'skuCode',
    width: 150,
  },
  {
    title: 'SKU ID',
    dataIndex: 'skuId',
    width: 150,
  },
  {
    title: $i18n.get({ id: 'waveAnalyze.skuBatchNos', dm: '指定批次号' }),
    dataIndex: 'batchNo',
    width: 150,
    cell: (value) => {
      return <>{value === "''" ? '-' : value}</>;
    },
  },

  {
    title: $i18n.get({ id: 'ProcessingType.four_px', dm: '处理类型' }),
    dataIndex: 'operationCode',
    width: 150,
    cell: (value: string) => vToLabel(VAS_DEAL_TYPE, value),
  },

  {
    title: $i18n.get({ id: 'DemandQuantity', dm: '需求数量' }),
    dataIndex: 'operationQty',
    width: 150,
  },
];

/**
 * ===> 清点库存
 */
const TakeStockColumn: TableColumnProps[] = [
  {
    title: 'No.',
    width: 70,
    cell: (value: string, index: number) => index + 1,
  },

  {
    title: $i18n.get({ id: 'AssociatedNumber', dm: '关联单号' }),
    dataIndex: 'relativeBusinessNo',
    width: 150,
  },

  {
    title: $i18n.get({ id: 'txt_sku_code', dm: 'SKU编码' }),
    dataIndex: 'skuCode',
    width: 150,
  },
  {
    title: 'SKU ID',
    dataIndex: 'skuId',
    width: 150,
  },
  {
    title: $i18n.get({ id: 'waveAnalyze.skuBatchNos', dm: '指定批次号' }),
    dataIndex: 'batchNo',
    width: 150,
    cell: (value) => {
      return <>{value === "''" ? '-' : value}</>;
    },
  },

  {
    title: $i18n.get({ id: 'ProcessingType.four_px', dm: '处理类型' }),
    dataIndex: 'operationCode',
    width: 150,
    cell: (value: string) => vToLabel(VAS_DEAL_TYPE, value),
  },

  {
    title: (
      <>
        {$i18n.get({ id: 'DemandQuantity', dm: '需求数量' })}

        <br />
        {$i18n.get({ id: 'SystemInventory', dm: '（系统库存数）' })}
      </>
    ),

    dataIndex: 'operationQty',
    width: 150,
  },

  {
    title: (
      <>
        {$i18n.get({ id: 'NumberOfOperations', dm: '操作数量' })}

        <br />
        {$i18n.get({ id: 'ActualNumberInLibrary', dm: '（实际在库数）' })}
      </>
    ),

    dataIndex: 'actualOperationQty',
    slot: 'actualOperationQty',
    width: 150,
  },
];

/**
 * ===> 商品核实
 */
const CheckColumn: TableColumnProps[] = [
  {
    title: 'No.',
    width: 70,
    cell: (value: string, index: number) => index + 1,
  },

  {
    title: $i18n.get({ id: 'AssociatedNumber', dm: '关联单号' }),
    dataIndex: 'relativeBusinessNo',
    width: 150,
  },

  {
    title: $i18n.get({ id: 'txt_sku_code', dm: 'SKU编码' }),
    dataIndex: 'skuCode',
    width: 150,
  },
  {
    title: 'SKU ID',
    dataIndex: 'skuId',
    width: 150,
  },
  {
    title: $i18n.get({ id: 'waveAnalyze.skuBatchNos', dm: '指定批次号' }),
    dataIndex: 'batchNo',
    width: 150,
    cell: (value) => {
      return <>{value === "''" ? '-' : value}</>;
    },
  },

  {
    title: $i18n.get({ id: 'ProcessingType.four_px', dm: '处理类型' }),
    dataIndex: 'operationCode',
    width: 150,
    cell: (value: string) => vToLabel(VAS_DEAL_TYPE, value),
  },

  {
    title: (
      <>
        {$i18n.get({ id: 'DemandQuantity', dm: '需求数量' })}

        <br />
        {$i18n.get({ id: 'VerifiedQuantity', dm: '（核实数量）' })}
      </>
    ),

    dataIndex: 'operationQty',
    width: 150,
  },

  {
    title: $i18n.get({ id: 'NumberOfOperations', dm: '操作数量' }),
    dataIndex: 'actualOperationQty',
    slot: 'actualOperationQty',
    width: 150,
  },
];

/**
 * ===> 核实材积重量
 */
const VerifyVolumeColumn: TableColumnProps[] = [
  {
    title: 'No.',
    width: 70,
    cell: (value: string, index: number) => index + 1,
  },

  {
    title: $i18n.get({ id: 'AssociatedNumber', dm: '关联单号' }),
    dataIndex: 'relativeBusinessNo',
    width: 150,
  },

  {
    title: $i18n.get({ id: 'txt_sku_code', dm: 'SKU编码' }),
    dataIndex: 'skuCode',
    width: 150,
  },
  {
    title: 'SKU ID',
    dataIndex: 'skuId',
    width: 150,
  },
  {
    title: $i18n.get({ id: 'waveAnalyze.skuBatchNos', dm: '指定批次号' }),
    dataIndex: 'batchNo',
    width: 150,
    cell: (value) => {
      return <>{value === "''" ? '-' : value}</>;
    },
  },

  {
    title: $i18n.get({ id: 'ProcessingType.four_px', dm: '处理类型' }),
    dataIndex: 'operationCode',
    width: 150,
    cell: (value: string) => vToLabel(VAS_DEAL_TYPE, value),
  },

  {
    title: (
      <>
        <span>{$i18n.get({ id: 'SystemLengthWidthAndHeight', dm: '系统长宽高' })}(CM)</span>
        <br />
        <span>{$i18n.get({ id: '31255237750820864.CNTM', dm: '重量' })}(KG)</span>
      </>
    ),

    width: 150,
    cell: (value: string, index: number, record) => {
      return (
        <>
          <div>{dealSize(record?.length, record?.width, record?.height)}</div>
          <div>{record?.weight || ''}</div>
        </>
      );
    },
  },

  {
    title: `${$i18n.get({ id: 'ActualLength', dm: '实际长' })}(CM)`,
    dataIndex: 'actualLength',
    slot: 'actualLength',
    width: 150,
  },

  {
    title: `${$i18n.get({ id: 'ActualWidth', dm: '实际宽' })}(CM)`,
    dataIndex: 'actualWidth',
    slot: 'actualWidth',
    width: 150,
  },

  {
    title: `${$i18n.get({ id: 'ActualHigh', dm: '实际高' })}(CM)`,
    dataIndex: 'actualHeight',
    slot: 'actualHeight',
    width: 150,
  },

  {
    title: `${$i18n.get({ id: '31255234286325760.CNTM', dm: '实际重量' })}(KG)`,
    dataIndex: 'actualWeight',
    slot: 'actualWeight',
    width: 150,
  },
];

/**
 * ===> 拆箱上架
 */
const UnpackingShelvingColumn: TableColumnProps[] = [
  {
    title: 'No.',
    width: 70,
    dataIndex: 'No',
    // cell: (value: string, index: number) => index + 1,
  },
  {
    title: $i18n.get({ id: 'FullBoxSKUCode', dm: '整箱SKU编码' }),
    dataIndex: 'skuCode',
    width: 190,
  },
  {
    title: $i18n.get({ id: 'WholeBoxSKUID', dm: '整箱SKUID' }),
    dataIndex: 'skuId',
    width: 190,
  },

  {
    title: $i18n.get({ id: 'WholeBoxSKUQuantity', dm: '整箱SKU数量' }),
    dataIndex: 'operationQty',
    width: 150,
  },
  {
    title: $i18n.get({ id: 'FullBoxSkuBatchNumber', dm: '整箱SKU批次号' }),
    dataIndex: 'batchNo',
    width: 150,
  },

  {
    title: $i18n.get({ id: 'InventoryOfCommodityBarCodes', dm: '清点商品条码' }),
    dataIndex: 'actualSkuProductCode',
    width: 150,
  },

  {
    title: $i18n.get({ id: 'RetailSKUCoding', dm: '零售SKU编码' }),
    dataIndex: 'actualSkuCode',
    width: 150,
  },
  {
    title: $i18n.get({ id: 'RetailSKUID', dm: '零售SKUID' }),
    dataIndex: 'actualSkuId',
    width: 150,
  },

  {
    title: $i18n.get({ id: 'RetailSKUQuantity', dm: '零售SKU数量' }),
    dataIndex: 'actualOperationQty',
    width: 150,
  },
  {
    title: $i18n.get({ id: 'RetailSKUBatchNumber', dm: '零售SKU批次号' }),
    dataIndex: 'actualBatchNo',
    width: 150,
  },

  {
    title: $i18n.get({ id: 'ProcessingType.four_px', dm: '处理类型' }),
    dataIndex: 'operationCode',
    width: 150,
    cell: (value: string) => vToLabel(VAS_DEAL_TYPE, value),
  },

  {
    title: $i18n.get({ id: 'RetailSKUQualityType', dm: '零售SKU质量类型' }),
    dataIndex: 'actualSkuStockQuality',
    width: 150,
    cell: (value: string) => vToLabel(VAS_SKU_STOCK_QUALITY, value),
  },
];

/**
 * ===> 合箱上架
 */
const TanksColumn: TableColumnProps[] = [
  {
    title: 'No.',
    width: 70,
    cell: (value: string, index: number) => index + 1,
  },

  {
    title: $i18n.get({ id: 'FullBoxSKUCode', dm: '整箱SKU编码' }),
    dataIndex: 'actualSkuCode',
    width: 190,
  },
  {
    title: $i18n.get({ id: 'WholeBoxSKUID', dm: '整箱SKUID' }),
    dataIndex: 'actualSkuId',
    width: 190,
  },

  {
    title: $i18n.get({ id: 'WholeBoxSKUQuantity', dm: '整箱SKU数量' }),
    dataIndex: 'actualOperationQty',
    width: 150,
  },
  {
    title: $i18n.get({ id: 'FullBoxSkuBatchNumber', dm: '整箱SKU批次号' }),
    dataIndex: 'actualBatchNo',
    width: 150,
  },

  {
    title: $i18n.get({ id: 'RetailSKUCoding', dm: '零售SKU编码' }),
    dataIndex: 'skuCode',
    width: 150,
  },
  {
    title: $i18n.get({ id: 'RetailSKUID', dm: '零售SKUID' }),
    dataIndex: 'skuId',
    width: 150,
  },
  {
    title: $i18n.get({ id: 'RetailSKUBatchNumber', dm: '零售SKU批次号' }),
    dataIndex: 'batchNo',
    width: 150,
  },

  {
    title: $i18n.get({ id: 'RetailSKUQuantity', dm: '零售SKU数量' }),
    dataIndex: 'operationQty',
    width: 150,
  },

  {
    title: $i18n.get({ id: 'ProcessingType.four_px', dm: '处理类型' }),
    dataIndex: 'operationCode',
    width: 150,
    cell: (value: string) => vToLabel(VAS_DEAL_TYPE, value),
  },
];

/**
 * ===> 一对一换标
 */
const ChangeLabelColumn: TableColumnProps[] = [
  {
    title: 'No.',
    width: 70,
    cell: (value: string, index: number) => index + 1,
  },

  {
    title: $i18n.get({ id: 'SkuCodeBeforeReplacement', dm: '更换前sku编码' }),
    dataIndex: 'skuCode',
    width: 150,
  },
  {
    title: $i18n.get({ id: 'ReplaceFrontSkuId', dm: '更换前skuId' }),
    dataIndex: 'skuId',
    width: 150,
  },
  {
    title: $i18n.get({ id: 'ReplaceFrontSKUBatchNumber', dm: '更换前SKU批次号' }),
    dataIndex: 'batchNo',
    width: 150,
  },

  {
    title: $i18n.get({ id: 'SkuCodeAfterReplacement', dm: '更换后sku编码' }),
    dataIndex: 'actualSkuCode',
    width: 150,
  },
  {
    title: $i18n.get({ id: 'ReplaceTheRearSkuId', dm: '更换后skuId' }),
    dataIndex: 'actualSkuId',
    width: 150,
  },
  {
    title: $i18n.get({ id: 'ReplaceRearSKUBatchNumber', dm: '更换后SKU批次号' }),
    dataIndex: 'actualBatchNo',
    width: 150,
  },

  {
    title: $i18n.get({ id: 'ChangeSkuBarcodeQuantity', dm: '更换sku条码数量' }),
    dataIndex: 'actualOperationQty',
    width: 150,
  },
];

const cellLocationCode = (value: string, index: number, record) => {
  return (
    <>
      {record?.vasInvs?.map((item, index) => {
        return (
          <p key={index} className={styles.tableborderp}>
            {item.locationCode}
          </p>
        );
      })}
    </>
  );
};

const cellStockQty = (value: string, index: number, record) => {
  return (
    <>
      {record?.vasInvs?.map((item, index) => {
        return (
          <p key={index} className={styles.tableborderp}>
            {item.stockQty}
          </p>
        );
      })}
    </>
  );
};

const VasMatchInventoryColumnWithStockQty: TableColumnProps[] = [
  {
    title: 'No.',
    width: 70,
    cell: (value: string, index: number) => index + 1,
  },

  {
    title: $i18n.get({ id: 'txt_sku_code', dm: 'SKU编码' }),
    dataIndex: 'skuCode',
    width: 180,
  },

  {
    title: 'SKU ID',
    dataIndex: 'skuId',
    width: 150,
  },
  {
    title: $i18n.get({ id: 'LocationMatching.four_px', dm: '匹配库位' }),
    dataIndex: 'locationCode',
    width: 150,
    cell: (value: string, index: number, record) => cellLocationCode(value, index, record),
    className: styles.tabletdclass,
  },

  {
    title: $i18n.get({ id: 'MatchingInventoryQuantity', dm: '匹配库存数量' }),
    dataIndex: 'stockQty',
    width: 150,
    cell: (value: string, index: number, record) => cellStockQty(value, index, record),
    className: styles.tabletdclass,
  },
  {
    title: $i18n.get({ id: 'MatchingInventoryBatchNumber', dm: '匹配库存批次号' }),
    dataIndex: 'batchNo',
    width: 150,
    className: styles.tabletdclass,
    cell: (value, index, record) => {
      return (
        <>
          {record?.vasInvs?.map((item, index) => {
            return (
              <p key={index} className={styles.tableborderp}>
                {item.batchNo}
              </p>
            );
          })}
        </>
      );
    },
  },

  {
    title: $i18n.get({ id: 'ProcessingType.four_px', dm: '处理类型' }),
    dataIndex: 'operationCode',
    width: 150,
    cell: (value: string) => vToLabel(VAS_DEAL_TYPE, value),
  },

  {
    title: $i18n.get({ id: 'DemandQuantity', dm: '需求数量' }),
    dataIndex: 'operationQty',
    width: 140,
  },

  // {
  //   title: $i18n.get({ id: 'NumberOfOperations', dm: '操作数量' }),
  //   dataIndex: 'actualOperationQty',
  //   slot: 'actualOperationQty',
  //   width: 140,
  // },
];

const VasMatchInventoryColumnWithoutStockQty: TableColumnProps[] = [
  {
    title: 'No.',
    width: 70,
    cell: (value: string, index: number) => index + 1,
  },

  {
    title: $i18n.get({ id: 'txt_sku_code', dm: 'SKU编码' }),
    dataIndex: 'skuCode',
    width: 180,
  },

  {
    title: 'SKU ID',
    dataIndex: 'skuId',
    width: 150,
  },
  {
    title: $i18n.get({ id: 'LocationMatching.four_px', dm: '匹配库位' }),
    dataIndex: 'locationCode',
    width: 150,
    cell: (value: string, index: number, record) => cellLocationCode(value, index, record),
    className: styles.tabletdclass,
  },
  {
    title: $i18n.get({ id: 'MatchingInventoryBatchNumber', dm: '匹配库存批次号' }),
    dataIndex: 'batchNo',
    width: 150,
    className: styles.tabletdclass,
    cell: (value, index, record) => {
      return (
        <>
          {record?.vasInvs?.map((item, index) => {
            return (
              <p key={index} className={styles.tableborderp}>
                {item.batchNo}
              </p>
            );
          })}
        </>
      );
    },
  },

  {
    title: $i18n.get({ id: 'ProcessingType.four_px', dm: '处理类型' }),
    dataIndex: 'operationCode',
    width: 150,
    cell: (value: string) => vToLabel(VAS_DEAL_TYPE, value),
  },

  {
    title: $i18n.get({ id: 'DemandQuantity', dm: '需求数量' }),
    dataIndex: 'operationQty',
    width: 140,
  },

  // {
  //   title: $i18n.get({ id: 'NumberOfOperations', dm: '操作数量' }),
  //   dataIndex: 'actualOperationQty',
  //   slot: 'actualOperationQty',
  //   width: 140,
  // },
];

/**
 * 需要合并的单元格
 */
// 拍照
const photoCell = ['No', 'relativeBusinessNo', 'skuCode'];
// 标签处理
const labelCell = ['No', 'relativeBusinessNo', 'skuCode'];
// 商品核实
const checkCell = ['No', 'relativeBusinessNo', 'skuCode'];
// 拆箱
const UnpackingShelvingCell = ['No', 'skuCode', 'operationQty'];
// 合箱上架
const TanksCell = ['No', 'actualSkuCode', 'actualOperationQty', 'operationCode', 'actualBatchNo'];

/**
 * 不展示提交按钮的子状态
 * 子状态（待匹配MP/待拣选PP/待换表CL/待上架PA）的不应该显示提交按钮
 */
const unSubmitType = ['MP', 'PP', 'CL', 'PA'];

export {
  basicItems,
  RepackageColumn,
  PhotoColumn,
  LabelColumn,
  TakeStockColumn,
  CheckColumn,
  VerifyVolumeColumn,
  UnpackingShelvingColumn,
  TanksColumn,
  ChangeLabelColumn,
  photoCell,
  labelCell,
  checkCell,
  UnpackingShelvingCell,
  TanksCell,
  unSubmitType,
  VasMatchInventoryColumnWithoutStockQty,
  VasMatchInventoryColumnWithStockQty,
};
